import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { api, ApiError, setSignedOutHandler } from '../api/client'
import { getSession, isRootAdmin, setSession, type Session } from '../api/session'
import type { TokenResponse } from '../api/types'

/** Roles this dashboard admits. Coordinators see only the delivery pages. */
const WEB_ROLES = ['admin', 'coordinator'] as const

interface AuthValue {
  session: Session | null
  /** The admin role — everything beyond the delivery pages is admin-only. */
  isAdmin: boolean
  /** The root `admin` account, which alone sees usage analytics and margins. */
  rootAdmin: boolean
  signIn: (username: string, password: string) => Promise<void>
  signOut: () => void
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>
}

const AuthContext = createContext<AuthValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setCurrent] = useState<Session | null>(() => getSession())

  const signOut = useCallback(() => {
    setSession(null)
    setCurrent(null)
  }, [])

  // The client signs out when a refresh fails, which can happen inside any
  // request; this keeps React's copy of the session in step with that.
  useEffect(() => {
    setSignedOutHandler(() => setCurrent(null))
  }, [])

  const signIn = useCallback(async (username: string, password: string) => {
    const tokens = await api.post<TokenResponse>(
      '/auth/login',
      { username, password },
      { anonymous: true },
    )

    // The API lets every role log in; this dashboard is for admins and
    // coordinators, so the check lives here. The endpoints enforce each
    // role's access again server-side.
    if (!(WEB_ROLES as readonly string[]).includes(tokens.role)) {
      throw new ApiError(403, 'This dashboard is for admin and coordinator accounts only.')
    }

    const next: Session = {
      accessToken: tokens.access_token,
      refreshToken: tokens.refresh_token,
      staffId: tokens.staff_id,
      username: tokens.username,
      firstName: tokens.first_name,
      lastName: tokens.last_name,
      role: tokens.role,
      mustChangePassword: tokens.must_change_password,
    }
    setSession(next)
    setCurrent(next)
  }, [])

  const changePassword = useCallback(async (oldPassword: string, newPassword: string) => {
    await api.post('/auth/change-password', {
      old_password: oldPassword,
      new_password: newPassword,
    })
    const current = getSession()
    if (current) {
      const next = { ...current, mustChangePassword: false }
      setSession(next)
      setCurrent(next)
    }
  }, [])

  const value = useMemo<AuthValue>(
    () => ({
      session,
      isAdmin: session?.role === 'admin',
      rootAdmin: isRootAdmin(session),
      signIn,
      signOut,
      changePassword,
    }),
    [session, signIn, signOut, changePassword],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth must be used inside AuthProvider')
  return value
}
